import {
  ControlModeTemplate,
  ControlModeTemplateField,
  ControlModeTemplateFieldsType,
  IStringInputFieldControlModeTemplate,
  INumberInputFieldControlModeTemplate,
  IBooleanInputFieldControlModeTemplate,
  ISelectInputFieldControlModeTemplate,
  ControlModeTemplateSettings,
} from '@src/types/ControlModes';
import { FormValue } from '../context/types';

/**
 * Prepares a ControlModeTemplate object for API submission by omitting the id property
 */
export const prepareTemplateForApiSubmission = (
  template: ControlModeTemplate,
): Omit<ControlModeTemplate, 'id'> => {
  const { id, ...templateWithoutId } = template;
  return templateWithoutId;
};

/**
 * Prepares updated settings with proper DTO types
 */
export const prepareUpdatedSettings = (
  settings: ControlModeTemplateSettings[],
  formValues: Record<string, FormValue>,
): ControlModeTemplateSettings[] => {
  return settings.map((setting: ControlModeTemplateSettings) => {
    // Map all fields, updating values where applicable
    const updatedFields: ControlModeTemplateField[] = setting.fields.map(
      (field: ControlModeTemplateField) => {
        // Use the formValues if provided, otherwise keep original defaultValue
        const defaultValue =
          formValues[field.controllerField] !== undefined
            ? formValues[field.controllerField]
            : field.defaultValue;

        // Create the proper field structure based on field type - only include properties valid for each type
        switch (field.type) {
          case ControlModeTemplateFieldsType.STRING: {
            // For string fields, only include these specific properties - no listOptions or range
            const stringField: IStringInputFieldControlModeTemplate = {
              uiLabel: field.uiLabel,
              controllerField: field.controllerField,
              quickConfig: field.quickConfig || false,
              type: ControlModeTemplateFieldsType.STRING,
            };

            if (defaultValue !== undefined) {
              // Ensure defaultValue is a string
              stringField.defaultValue = String(defaultValue);
            }

            return stringField;
          }
          case ControlModeTemplateFieldsType.NUMBER: {
            // For number fields, include range but not listOptions
            const numberField: INumberInputFieldControlModeTemplate = {
              uiLabel: field.uiLabel,
              controllerField: field.controllerField,
              quickConfig: field.quickConfig || false,
              type: ControlModeTemplateFieldsType.NUMBER,
            };

            if (field.range) {
              numberField.range = field.range;
            }

            if (defaultValue !== undefined) {
              // Ensure defaultValue is a number
              numberField.defaultValue = Number(defaultValue);
            }

            return numberField;
          }
          case ControlModeTemplateFieldsType.BOOLEAN: {
            // For boolean fields, don't include range or listOptions
            const booleanField: IBooleanInputFieldControlModeTemplate = {
              uiLabel: field.uiLabel,
              controllerField: field.controllerField,
              quickConfig: field.quickConfig || false,
              type: ControlModeTemplateFieldsType.BOOLEAN,
            };

            if (defaultValue !== undefined) {
              // Ensure defaultValue is a boolean
              booleanField.defaultValue = Boolean(defaultValue);
            }

            return booleanField;
          }
          case ControlModeTemplateFieldsType.SELECT: {
            // For select fields, include listOptions but not range
            const selectField: ISelectInputFieldControlModeTemplate = {
              uiLabel: field.uiLabel,
              controllerField: field.controllerField,
              quickConfig: field.quickConfig || false,
              type: ControlModeTemplateFieldsType.SELECT,
              listOptions: field.listOptions || [],
            };

            if (defaultValue !== undefined) {
              // Ensure defaultValue is a string
              selectField.defaultValue = String(defaultValue);
            }

            return selectField;
          }
          default: {
            // This should never happen, but TypeScript needs us to handle this case
            const stringField: IStringInputFieldControlModeTemplate = {
              uiLabel: 'Unknown field',
              controllerField: 'unknown',
              quickConfig: false,
              type: ControlModeTemplateFieldsType.STRING,
            };
            return stringField;
          }
        }
      },
    );

    return {
      controllerField: setting.controllerField,
      uiLabel: setting.uiLabel,
      fields: updatedFields,
    };
  });
};
